import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Typography } from '@mui/material';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

// The worker is copied untouched into public/ by the copy-pdf-worker script.
// Bundling it through webpack runs Babel over it, which breaks it.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

// One page: a canvas drawn when it scrolls near the screen, with the
// overlay (signing fields) positioned on top in PDF-point percentages.
const PdfPage = ({ pdf, pageNumber, size, pageCount, renderOverlay, pageRef }) => {
  const { t } = useTranslation();
  const boxRef = useRef(null);
  const canvasRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    const el = boxRef.current;
    if (!el || visible) return undefined;
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '800px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible || rendered) return undefined;
    let cancelled = false;
    let task = null;
    (async () => {
      const page = await pdf.getPage(pageNumber);
      if (cancelled) return;
      const cssWidth = boxRef.current?.clientWidth || 800;
      // Sharp on high-density screens, but capped to keep phones' memory in check.
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const scale = (cssWidth / size.width) * ratio;
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      task = page.render({ canvasContext: canvas.getContext('2d'), viewport });
      await task.promise;
      if (!cancelled) setRendered(true);
    })().catch(() => {});
    return () => {
      cancelled = true;
      if (task) task.cancel();
    };
  }, [visible, rendered, pdf, pageNumber, size.width]);

  return (
    <Box ref={pageRef} sx={{ mb: 2 }}>
      <Box
        ref={boxRef}
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${size.width} / ${size.height}`,
          bgcolor: '#fff',
          boxShadow: 2,
        }}
      >
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} aria-hidden="true" />
        {!rendered && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} aria-label={t('esign.sign.loadingPage')} />
          </Box>
        )}
        {renderOverlay && renderOverlay(pageNumber, size)}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
        {t('esign.sign.page', { page: pageNumber, pages: pageCount })}
      </Typography>
    </Box>
  );
};

/**
 * Renders a PDF (ArrayBuffer) page by page with pdf.js.
 * renderOverlay(pageNumber, { width, height }) draws fields over a page.
 * pageRefs receives each page's outer element, keyed by page number.
 */
const PdfDocumentView = ({ data, renderOverlay, onError, pageRefs }) => {
  const [pdf, setPdf] = useState(null);
  const [sizes, setSizes] = useState([]);

  useEffect(() => {
    if (!data) return undefined;
    let cancelled = false;
    // pdf.js takes ownership of the buffer it's given, so pass a copy.
    const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(data.slice(0)), isEvalSupported: false });
    (async () => {
      const doc = await loadingTask.promise;
      const pageSizes = [];
      for (let i = 1; i <= doc.numPages; i += 1) {
        const page = await doc.getPage(i);
        const [x0, y0, x1, y1] = page.view;
        pageSizes.push({ width: x1 - x0, height: y1 - y0 });
      }
      if (!cancelled) {
        setSizes(pageSizes);
        setPdf(doc);
      }
    })().catch((err) => {
      if (!cancelled && onError) onError(err);
    });
    return () => {
      cancelled = true;
      loadingTask.destroy();
    };
  }, [data, onError]);

  if (!pdf) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {sizes.map((size, i) => (
        <PdfPage
          key={i + 1}
          pdf={pdf}
          pageNumber={i + 1}
          size={size}
          pageCount={sizes.length}
          renderOverlay={renderOverlay}
          pageRef={(el) => {
            if (pageRefs) pageRefs.current[i + 1] = el;
          }}
        />
      ))}
    </Box>
  );
};

export default PdfDocumentView;
